import { forwardRef, useCallback } from "react";

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";

// --- UI Primitives ---
import type { ButtonProps } from "@/components/tiptap-ui-primitive/button";
import { Button } from "@/components/tiptap-ui-primitive/button";

// --- Icons ---
import { MermaidIcon } from "@/components/tiptap-icons/mermaid-icon";

export interface MermaidButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
}

/**
 * Toolbar button to insert a Mermaid diagram block.
 * Uses inline editing - click the diagram to edit Mermaid code directly.
 */
export const MermaidButton = forwardRef<HTMLButtonElement, MermaidButtonProps>(
  ({ text, onClick, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor();

    const label = "Insert Mermaid diagram";

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        
        if (!editor) {
          return;
        }

        try {
          const commands = (editor as any).commands;

          if (typeof commands?.insertMermaid !== "function") {
            console.error("MermaidButton: insertMermaid command not available");
            return;
          }

          const defaultCode = `flowchart LR
  A --> B`;

          const from = editor.state.selection.from;

          // Insert the node first, then select it
          const insertResult = editor
            .chain()
            .focus()
            .insertMermaid({ code: defaultCode })
            .run();

          if (!insertResult) {
            console.error("MermaidButton: insertMermaid command returned false");
            return;
          }

          // Find the inserted node and select it
          const doc = editor.state.doc;
          let nodePos = from;
          let found = false;
          
          // Look for the mermaid node starting from insertion point
          for (let i = 0; i < Math.min(10, doc.content.size - from); i++) {
            const checkPos = from + i;
            const node = doc.nodeAt(checkPos);
            if (node && node.type.name === 'mermaidDiagram') {
              nodePos = checkPos;
              found = true;
              break;
            }
          }

          if (found) {
            editor.chain().setNodeSelection(nodePos).run();
          }
        } catch (err) {
          console.error("MermaidButton: insert failed", err);
        }
      },
      [editor, onClick],
    );

    if (!editor) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        <>
          <MermaidIcon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      </Button>
    );
  },
);

MermaidButton.displayName = "MermaidButton";
